import {
    BaseComponent,
    BlueprintLocation,
    ColorModel,
    HazardousModel,
    PhotoModel,
    RiskConstruction,
    RoomModel,
    ValueStatus,
    ValueWithMetadata
} from "./protocols";
import {
    BaseType,
    BuildComponentType,
    CardinalDirection,
    ElevatorType,
    Material,
    RoofShape,
    RoomType,
    UtilityCategory
} from "./Enums";
import {translate} from "./EnumTranslations";
import {AnyGeometryShape, computeShapeArea} from "./GeometryShapes";

export class BaseComponentImpl implements BaseComponent {
    key: string;
    type: BaseType;
    createdBy: string;
    createdWhen: string;
    comment: ValueWithMetadata<string>;
    photos?: { [key: string]: PhotoModel };
    specificType?: BuildComponentType;
    condition?: ValueWithMetadata<number>;
    buildingRisks?: ValueWithMetadata<RiskConstruction>;
    material?: ValueWithMetadata<Material[]>;
    color?: ValueWithMetadata<ColorModel>;
    areaShapes?: ValueWithMetadata<AnyGeometryShape[]>;
    cardinalDirection?: ValueWithMetadata<CardinalDirection>;
    depth?: ValueWithMetadata<number>;
    insulation?: ValueWithMetadata<number>;
    label?: string;
    wallCount?: ValueWithMetadata<number>;
    hazardous?: ValueWithMetadata<HazardousModel>;
    canHaveWindows?: Boolean;
    canHaveDoors?: Boolean;
    roofSlope?: ValueWithMetadata<number>;
    roofShape?: ValueWithMetadata<RoofShape>;
    parentRoomKeys?: ValueWithMetadata<string[]>;
    typeKey?: ValueWithMetadata<string>;
    roomKey?: string;
    utilityCategory?: UtilityCategory;
    elevatorType?: ValueWithMetadata<ElevatorType>;

    constructor(data: any) {
        this.key = data.key;
        this.type = data.type;
        this.createdBy = data.createdBy;
        this.createdWhen = data.createdWhen;
        this.comment = data.comment;
        if (data.photos) this.photos = data.photos;
        if (data.specificType !== undefined) this.specificType = data.specificType;
        if (data.condition !== undefined) this.condition = data.condition;
        if (data.buildingRisks !== undefined) this.buildingRisks = data.buildingRisks;
        if (data.material !== undefined) this.material = data.material;
        if (data.color !== undefined) this.color = data.color;
        if (data.areaShapes !== undefined) this.areaShapes = data.areaShapes;
        if (data.cardinalDirection !== undefined) this.cardinalDirection = data.cardinalDirection;
        if (data.depth !== undefined) this.depth = data.depth;
        if (data.insulation !== undefined) this.insulation = data.insulation;
        if (data.label !== undefined) this.label = data.label;
        if (data.wallCount !== undefined) this.wallCount = data.wallCount;
        if (data.hazardous !== undefined) this.hazardous = data.hazardous;
        if (data.canHaveWindows !== undefined) this.canHaveWindows = data.canHaveWindows;
        if (data.canHaveDoors !== undefined) this.canHaveDoors = data.canHaveDoors;
        if (data.roofSlope !== undefined) this.roofSlope = data.roofSlope;
        if (data.roofShape !== undefined) this.roofShape = data.roofShape;
        if (data.parentRoomKeys !== undefined) this.parentRoomKeys = data.parentRoomKeys;
        if (data.typeKey !== undefined) this.typeKey = data.typeKey;
        if (data.roomKey !== undefined) this.roomKey = data.roomKey;
        if (data.utilityCategory !== undefined) this.utilityCategory = data.utilityCategory;
        if (data.elevatorType !== undefined) this.elevatorType = data.elevatorType;
    }

    displayName(): string {
        let name = "";
        if (this.label) {
            name = this.label;
        } else if (this.roofShape?.value) {
            name = translate(this.roofShape.value)
        } else if (this.specificType) {
            name = translate(this.specificType);
        } else {
            name = translate(this.type);
        }
        if (this.cardinalDirection?.value) {
            name = `${name} (${translate(this.cardinalDirection.value)})`
        }
        return name;
    }

    /**
     * Calculates the total area of all shapes in areaShapes
     * @returns The sum of all shape areas or null if no areas are set
     */
    totalArea(): number | null {
        if (!this.areaShapes || !this.areaShapes.value || this.areaShapes.value.length === 0) {
            return null;
        }

        let totalArea = 0;
        for (const shape of this.areaShapes.value) {
            totalArea += computeShapeArea(shape);
        }

        return totalArea;
    }
}

export class RoomImpl implements RoomModel {
    key: string = "";
    number?: string;
    label?: string;
    levelKey?: string;
    location?: BlueprintLocation;
    type?: RoomType;
    ceilingHeight: ValueWithMetadata<number>;
    photos?: { [key: string]: PhotoModel };

    constructor(data: RoomModel) {
        this.ceilingHeight = new ValueWithMetadataImpl<number>();
        Object.assign(this, data);
    }

    displayName(): string {
        if (this.label && this.number) {
            return `Rum ${this.number} - ${this.label}`
        }

        if (this.number) {
            return `Rum ${this.number}`
        }

        if (this.label) {
            return this.label
        }

        if (this.type) {
            return translate(this.type)
        }

        return "Odefinierat rum"
    }
}

export class ValueWithMetadataImpl<T> implements ValueWithMetadata<T> {
    readonly value: T | null;
    readonly updatedWhen: string | null;
    readonly updatedBy: string | null;
    readonly status: ValueStatus;

    constructor(value: T | null = null, updatedWhen: string | null = null,
                updatedBy: string | null = null, status: ValueStatus = ValueStatus.notSet) {
        this.value = value;
        this.updatedWhen = updatedWhen;
        this.updatedBy = updatedBy;
        this.status = status;
    }
}